"use strict";

require('jquery');

/**
 * Sticky
 *
 * Allow elements to be sticky
 *
 * ### HTML:
 *
 * <!-- add "enable sticky" -->
 * <div class="sticky" data-sticky-enable="<some id>">
 *     <div>
 *         <!-- add your sticky html template here -->
 *     </div>
 * </div>
 *
 * <!-- optional: add below "enable sticky" a "disable sticky" -->
 * <div data-sticky-disable="<some id>"></div>
 *
 * Sticky html template becomes sticky when html element with "data-sticky-enable" is above viewport
 * and becomes non-sticky when the html with "data-sticky-disable" is in or above viewport.
 * "data-sticky-disable" only works when the id of "data-sticky-disable" is the same as "data-sticky-enable".
 *
 * ### SCSS:
 *
 * .sticky.sticky--enable > div {
 *    position: fixed;
 * }
 *
 */
class Sticky {

    /**
     * Constructor
     *
     * Initialize
     */
    constructor() {
        // attributes
        this.dataEnableSticky = 'data-sticky-enable';
        this.dataDisableSticky = 'data-sticky-disable';
        this.dataInitSticky = 'data-sticky-init';

        // classes
        this.classNameEnableSticky = 'sticky--enable';
        this.classNameDisableSticky = 'sticky--disable';
        this.classNameDisabledByDisableSticky = 'sticky---disabled-by-sticky-disable';

        // elements
        this.observer = null;
        this.stickyEnableElements = document.querySelectorAll(`[${this.dataEnableSticky}]`);
        this.stickyDisableElements = document.querySelectorAll(`[${this.dataDisableSticky}]`);

        // configuration
        this.options = {
            root: null,
            rootMargin: '0px',
            threshold: 0
        }

        this._initObserver();
    }

    /**
     * Initialize intersection observer
     *
     * @private
     */
    _initObserver() {
        this.stickyEnableElements.forEach(stickyEnableElement => {
            const observer = new IntersectionObserver(this._callbackEnable.bind(this), this.options);
            observer.observe(stickyEnableElement);
        });

        this.stickyDisableElements.forEach(stickyDisableElement => {
            const observer = new IntersectionObserver(this._callbackDisable.bind(this), this.options);
            observer.observe(stickyDisableElement);
        });
    }

    /**
     * Callback for intersected data-sticky-enable elements
     *
     * @param {object} entries
     *
     * @private
     */
    _callbackEnable(entries) {
        entries.forEach(entry => {
            const elem = entry.target;
            if (true === entry.isIntersecting || entry.boundingClientRect.top > 0) {
                this._disable(elem);

                return;
            }

            this._enable(elem);
        });
    }

    /**
     * Callback for intersected data-sticky-disable elements
     *
     * @param {object} entries
     *
     * @private
     */
    _callbackDisable(entries) {
        entries.forEach(entry => {
            const elem = entry.target;

            if (false === elem.hasAttribute(this.dataInitSticky)) {
                elem.setAttribute(this.dataInitSticky, true);

                if (entry.boundingClientRect.top > window.innerHeight) return;
            }

            const attrValue = elem.getAttribute(this.dataDisableSticky);
            if (null === attrValue) return;

            this.stickyEnableElements.forEach(stickyEnableElement => {
                if (attrValue !== stickyEnableElement.getAttribute(this.dataEnableSticky)) return;

                if (true === entry.isIntersecting || entry.boundingClientRect.top < 0) {
                    this._disable(stickyEnableElement);
                    stickyEnableElement.classList.add(this.classNameDisabledByDisableSticky);

                    return;
                }

                this._enable(stickyEnableElement);
                stickyEnableElement.classList.remove(this.classNameDisabledByDisableSticky);
            })
        });
    }

    /**
     * Enable sticky element
     *
     * @param {Element} elem
     *
     * @private
     */
    _enable(elem) {
        elem.classList.remove(this.classNameDisableSticky);
        elem.classList.add(this.classNameEnableSticky);
    }

    /**
     * Disable sticky element
     *
     * @param {Element} elem
     *
     * @private
     */
    _disable(elem) {
        elem.classList.remove(this.classNameEnableSticky);
        elem.classList.add(this.classNameDisableSticky);
    }
}

export default Sticky;
